import { DqCard, DqCardContent } from "@decentriq/components";
import {
  type MatchingColumnFormat,
  type TableColumnFormatType,
  type TableColumnHashingAlgorithm,
} from "@decentriq/graphql/dist/types";
import {
  faBullseye,
  faClock,
  faCogs,
  faCopy,
  faHashtag,
  faHashtagLock,
  faUser,
  faWrench,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, IconButton, Stack, Tooltip, Typography } from "@mui/joy";
import { useSnackbar } from "notistack";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { DetailsGridValue, TimeAgoFormatted } from "components";
import { MediaDataRoomVersionSwitch } from "features/mediaDataRoom/components";
import { type MediaDataRoomParticipant } from "features/mediaDataRoom/models";
import { useCopyToClipboard } from "hooks";
import { matchingIdTypeAndHashingAlgorithmPresentation } from "models/dataRoom";
import AbIcon from "../assets/abIcon.svg";
import UsersIcon from "../assets/usersIcon.svg";
import MediaDataRoomConfigurationParticipantDrawer from "./MediaDataRoomConfigurationParticipantDrawer";
import MediaDataRoomConfigurationPermissionsTable from "./MediaDataRoomConfigurationPermissionsTable";
import MediaDataRoomV3ParticipantCard from "./MediaDataRoomV3ParticipantCard";
import ParticipantCard from "./ParticipantCard";

export interface MediaDataRoomConfigurationProps {
  collaborationTypes: string[];
  createdAt: string;
  createdBy?: string;
  hashMatchingIdWith: TableColumnHashingAlgorithm | null;
  matchingIdFormat: TableColumnFormatType;
  dataRoomName: string;
  dataRoomId: string;
  participants: MediaDataRoomParticipant[];
  showAbsoluteValues: boolean;
}
const MediaDataRoomConfiguration = memo<MediaDataRoomConfigurationProps>(
  ({
    collaborationTypes,
    createdAt,
    createdBy,
    hashMatchingIdWith,
    matchingIdFormat,
    dataRoomName,
    dataRoomId,
    participants,
    showAbsoluteValues,
  }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [, copyToClipboard] = useCopyToClipboard();
    const [selectedParticipant, setSelectedParticipant] =
      useState<MediaDataRoomParticipant | null>(null);
    const matchingId = useMemo(
      () =>
        matchingIdTypeAndHashingAlgorithmPresentation({
          matchingIdFormat: matchingIdFormat as unknown as MatchingColumnFormat,
          matchingIdHashingAlgorithm: hashMatchingIdWith,
        }),
      [matchingIdFormat, hashMatchingIdWith]
    );
    const handleSelectParticipant = useCallback(
      (participant: MediaDataRoomParticipant) => {
        setSelectedParticipant(participant);
      },
      [setSelectedParticipant]
    );
    const handleDrawerClose = useCallback(() => {
      setSelectedParticipant(null);
    }, [setSelectedParticipant]);
    return (
      <Fragment>
        <DqCard>
          <DqCardContent alignItems="flex-start">
            <Typography
              gutterBottom={true}
              level="body-sm"
              startDecorator={<AbIcon />}
              textColor="inherit"
            >
              <strong>Name</strong>
            </Typography>
            <DetailsGridValue value={dataRoomName} />
          </DqCardContent>
        </DqCard>
        <DqCard>
          <DqCardContent alignItems="flex-start">
            <Typography
              gutterBottom={true}
              level="body-sm"
              startDecorator={<FontAwesomeIcon icon={faHashtagLock} />}
              textColor="inherit"
            >
              <strong>Data clean room ID</strong>
            </Typography>
            <DetailsGridValue
              value={
                <Stack
                  alignItems="center"
                  direction="row"
                  gap={1}
                  justifyContent="space-between"
                  maxWidth="100%"
                >
                  <Typography
                    level="body-sm"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dataRoomId}
                  </Typography>
                  <Tooltip title={<span>Copy Data clean room ID</span>}>
                    <IconButton
                      onClick={() => {
                        if (dataRoomId) {
                          void copyToClipboard(dataRoomId);
                          enqueueSnackbar("Data clean room ID copied");
                        }
                      }}
                      size="sm"
                    >
                      <FontAwesomeIcon fixedWidth={true} icon={faCopy} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            />
          </DqCardContent>
        </DqCard>
        <DqCard>
          <DqCardContent alignItems="flex-start">
            <Typography
              gutterBottom={true}
              level="body-sm"
              startDecorator={<FontAwesomeIcon icon={faHashtag} />}
              textColor="inherit"
            >
              Matching ID
            </Typography>
            <DetailsGridValue value={matchingId} />
          </DqCardContent>
        </DqCard>
        <DqCard>
          <DqCardContent alignItems="flex-start">
            <Typography
              gutterBottom={true}
              level="body-sm"
              startDecorator={<FontAwesomeIcon icon={faClock} />}
              textColor="inherit"
            >
              Creation date
            </Typography>
            <DetailsGridValue value={<TimeAgoFormatted date={createdAt} />} />
          </DqCardContent>
        </DqCard>
        {createdBy && (
          <DqCard>
            <DqCardContent alignItems="flex-start">
              <Typography
                gutterBottom={true}
                level="body-sm"
                startDecorator={<FontAwesomeIcon icon={faUser} />}
                textColor="inherit"
              >
                Created by
              </Typography>
              <DetailsGridValue value={createdBy} />
            </DqCardContent>
          </DqCard>
        )}
        <DqCard>
          <DqCardContent alignItems="flex-start">
            <Typography
              gutterBottom={true}
              level="body-sm"
              startDecorator={<FontAwesomeIcon icon={faWrench} />}
              textColor="inherit"
            >
              Collaboration types
            </Typography>
            <DetailsGridValue value={collaborationTypes.join(", ")} />
          </DqCardContent>
        </DqCard>
        <DqCard>
          <DqCardContent alignItems="flex-start">
            <Typography
              gutterBottom={true}
              level="body-sm"
              startDecorator={<FontAwesomeIcon icon={faBullseye} />}
              textColor="inherit"
            >
              Absolute audience sizes
            </Typography>
            <DetailsGridValue value={showAbsoluteValues ? "Show" : "Hide"} />
          </DqCardContent>
        </DqCard>
        <DqCard>
          <DqCardContent alignItems="flex-start" sx={{ overflow: "hidden" }}>
            <Typography
              gutterBottom={true}
              level="body-sm"
              startDecorator={<UsersIcon />}
              textColor="inherit"
            >
              Participants
            </Typography>
            <Grid
              columnSpacing={2}
              container={true}
              rowSpacing={2}
              sx={{ pt: 2 }}
              xs={12}
            >
              {participants.map((participant) => (
                <Grid key={participant.id} sm={4} xs={12}>
                  <MediaDataRoomVersionSwitch
                    ab={<ParticipantCard participant={participant} />}
                    v3={
                      <MediaDataRoomV3ParticipantCard
                        onClick={() => handleSelectParticipant(participant)}
                        participant={participant}
                      />
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </DqCardContent>
        </DqCard>
        <MediaDataRoomVersionSwitch
          ab={null}
          v3={
            <Fragment>
              <DqCard>
                <DqCardContent alignItems="flex-start">
                  <Typography
                    gutterBottom={true}
                    level="body-sm"
                    startDecorator={<FontAwesomeIcon icon={faCogs} />}
                    textColor="inherit"
                  >
                    Permissions
                  </Typography>
                  <MediaDataRoomConfigurationPermissionsTable
                    participants={participants}
                  />
                </DqCardContent>
              </DqCard>
              <MediaDataRoomConfigurationParticipantDrawer
                onClose={handleDrawerClose}
                participant={selectedParticipant}
              />
            </Fragment>
          }
        />
      </Fragment>
    );
  }
);

MediaDataRoomConfiguration.displayName = "MediaDataRoomConfiguration";
export default MediaDataRoomConfiguration;
